import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input } from '@caruso/ui';
import { venuesService } from '@/services/venues.service';
import { venueReservationsService } from '@/services/venue-reservations.service';
import { MapPin, Users, DollarSign, Calendar, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { PricingType } from '@caruso/types';
import { cn } from '@caruso/ui';

export function PublicVenueDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [alternativeSuggestions, setAlternativeSuggestions] = useState<Array<{ startDate: string; endDate: string; startTime: string; endTime: string }>>([]);

  // Simulator state
  const [simulatorData, setSimulatorData] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
  });
  const [priceCalculation, setPriceCalculation] = useState<any>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Reservation form state
  const [reservationData, setReservationData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);

  const daysOfWeekLabels = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  const { data: venue, isLoading } = useQuery({
    queryKey: ['public-venue', id],
    queryFn: () => venuesService.getById(id!),
    enabled: !!id,
  });

  const nextImage = () => {
    if (venue && venue.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % venue.images.length);
    }
  };

  const previousImage = () => {
    if (venue && venue.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + venue.images.length) % venue.images.length);
    }
  };

  // Validate time is within venue operating hours
  const validateTimeWithinOperatingHours = (time: string, openTime: string, closeTime: string): boolean => {
    if (!time) return true;
    return time >= openTime && time <= closeTime;
  };

  // Generate alternative suggestions
  const generateAlternatives = (selectedDate: string, venue: any): Array<{ startDate: string; endDate: string; startTime: string; endTime: string }> => {
    const alternatives: Array<{ startDate: string; endDate: string; startTime: string; endTime: string }> = [];
    const date = new Date(selectedDate);
    const availableDays = venue.availableDays || [0, 1, 2, 3, 4, 5, 6];
    const openTime = venue.openTime || '08:00';
    const closeTime = venue.closeTime || '22:00';

    // Try to find 3 alternative dates in the next 14 days
    for (let i = 1; i <= 14 && alternatives.length < 3; i++) {
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + i);
      const dayOfWeek = nextDate.getDay();

      if (availableDays.includes(dayOfWeek)) {
        const dateStr = nextDate.toISOString().split('T')[0];
        alternatives.push({
          startDate: dateStr,
          endDate: dateStr,
          startTime: openTime,
          endTime: closeTime,
        });
      }
    }

    return alternatives;
  };

  const handleCheckAvailability = async () => {
    if (!id || !simulatorData.startDate || !simulatorData.endDate || !venue) return;

    setCheckingAvailability(true);
    setAvailabilityError(null);
    setAlternativeSuggestions([]);
    setAvailabilityChecked(false);
    setIsAvailable(false);
    setPriceCalculation(null);

    try {
      // Validate day of week
      const selectedDate = new Date(simulatorData.startDate);
      const dayOfWeek = selectedDate.getDay();
      const availableDays = venue.availableDays || [0, 1, 2, 3, 4, 5, 6];

      if (!availableDays.includes(dayOfWeek)) {
        setAvailabilityError(`Este ambiente não está disponível em ${daysOfWeekLabels[dayOfWeek]}. Dias disponíveis: ${availableDays.map((d: number) => daysOfWeekLabels[d]).join(', ')}.`);
        setAlternativeSuggestions(generateAlternatives(simulatorData.startDate, venue));
        setAvailabilityChecked(true);
        return;
      }

      // Validate operating hours
      const openTime = venue.openTime || '08:00';
      const closeTime = venue.closeTime || '22:00';

      if (simulatorData.startTime && !validateTimeWithinOperatingHours(simulatorData.startTime, openTime, closeTime)) {
        setAvailabilityError(`O horário de início deve estar entre ${openTime} e ${closeTime}.`);
        setAvailabilityChecked(true);
        return;
      }

      if (simulatorData.endTime && !validateTimeWithinOperatingHours(simulatorData.endTime, openTime, closeTime)) {
        setAvailabilityError(`O horário de término deve estar entre ${openTime} e ${closeTime}.`);
        setAvailabilityChecked(true);
        return;
      }

      // Check availability via API
      const availability = await venuesService.checkAvailability(id, simulatorData);

      if (!availability.available) {
        setAvailabilityError('Este horário não está disponível. Já existe uma reserva neste período.');
        setAlternativeSuggestions(generateAlternatives(simulatorData.startDate, venue));
        setAvailabilityChecked(true);
        return;
      }

      // If available, calculate price
      const result = await venuesService.calculatePrice(id, {
        ...simulatorData,
        discount: 0,
      });

      setPriceCalculation(result);
      setIsAvailable(true);
      setAvailabilityChecked(true);
      setShowReservationForm(true);
    } catch (error: any) {
      console.error('Error checking availability:', error);
      setAvailabilityError(error.response?.data?.message || 'Erro ao verificar disponibilidade. Tente novamente.');
      setAvailabilityChecked(true);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSelectAlternative = (alternative: { startDate: string; endDate: string; startTime: string; endTime: string }) => {
    setSimulatorData(alternative);
    setAvailabilityChecked(false);
    setAvailabilityError(null);
    setAlternativeSuggestions([]);
  };

  const handleReservation = async () => {
    if (!id) return;

    setSubmitting(true);
    try {
      await venueReservationsService.create({
        venueId: id,
        ...reservationData,
        ...simulatorData,
      });
      setReservationSuccess(true);
    } catch (error) {
      console.error('Error creating reservation:', error);
      alert('Erro ao criar reserva. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-brand-gold mb-4">Ambiente não encontrado</h1>
          <Button onClick={() => navigate('/ambientes')}>Voltar para listagem</Button>
        </div>
      </div>
    );
  }

  if (reservationSuccess) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="bg-brand-ink/50 border-brand-gold/20 max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-brand-gold mb-4">Reserva Solicitada!</h1>
          <p className="text-brand-ivory/70 mb-6">
            Sua solicitação de reserva foi enviada com sucesso. Entraremos em contato em breve para confirmar.
          </p>
          <div className="space-y-2">
            <Button onClick={() => navigate('/ambientes')} variant="premium" className="w-full">
              Ver Outros Ambientes
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
              Fazer Outra Reserva
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-ink">
      {/* Hero Image Gallery */}
      <div className="relative h-96 bg-brand-ink/80">
        {venue.images && venue.images.length > 0 ? (
          <>
            <img
              src={venue.images[currentImageIndex]?.url}
              alt={venue.name}
              className="w-full h-full object-cover"
            />
            {venue.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                  onClick={previousImage}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {venue.images.map((_, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        'w-2 h-2 rounded-full',
                        idx === currentImageIndex ? 'bg-brand-gold' : 'bg-white/50',
                      )}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-24 h-24 text-brand-gold/30" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-brand-gold mb-4">{venue.name}</h1>
              {venue.description && (
                <p className="text-brand-ivory/80 text-lg whitespace-pre-wrap">{venue.description}</p>
              )}
            </div>

            <div className="flex gap-6 text-brand-ivory/70">
              {venue.capacity && (
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>{venue.capacity} pessoas</span>
                </div>
              )}
              {venue.area && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{venue.area}m²</span>
                </div>
              )}
            </div>

            {venue.showPricePublicly !== false && (
              <div className="flex items-baseline gap-3">
                <DollarSign className="w-6 h-6 text-brand-gold" />
                <span className="text-4xl font-bold text-brand-gold">
                  {formatPrice(venue.defaultPrice)}
                </span>
                <span className="text-brand-ivory/60 text-xl">
                  /{venue.pricingType === PricingType.DAILY ? 'dia' : 'hora'}
                </span>
              </div>
            )}

            {/* Operating hours info */}
            <div className="flex items-center gap-2 text-brand-ivory/70 bg-brand-ink/30 p-3 rounded-lg">
              <Clock className="w-5 h-5 text-brand-gold" />
              <span>
                Funcionamento: {venue.openTime || '08:00'} às {venue.closeTime || '22:00'}
                {venue.availableDays && venue.availableDays.length < 7 && (
                  <span className="ml-2">
                    ({venue.availableDays.map((d: number) => daysOfWeekLabels[d].substring(0, 3)).join(', ')})
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Right Column - Simulator & Reservation */}
          <div className="space-y-6">
            <Card className="bg-brand-ink/50 border-brand-gold/20 sticky top-6">
              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-brand-gold flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Simular Reserva
                </h3>

                <div className="space-y-3">
                  {venue.pricingType === PricingType.HOURLY ? (
                    <>
                      {/* Por hora: uma única data + hora início/fim */}
                      <div>
                        <label className="block text-sm font-medium text-brand-ivory mb-2">Data</label>
                        <Input
                          type="date"
                          value={simulatorData.startDate}
                          onChange={(e) => setSimulatorData({
                            ...simulatorData,
                            startDate: e.target.value,
                            endDate: e.target.value
                          })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-ivory mb-2">Hora Início</label>
                        <Input
                          type="time"
                          value={simulatorData.startTime}
                          onChange={(e) => setSimulatorData({ ...simulatorData, startTime: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-ivory mb-2">Hora Fim</label>
                        <Input
                          type="time"
                          value={simulatorData.endTime}
                          onChange={(e) => setSimulatorData({ ...simulatorData, endTime: e.target.value })}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Por dia: apenas data (horários são automáticos do ambiente) */}
                      <div>
                        <label className="block text-sm font-medium text-brand-ivory mb-2">Data</label>
                        <Input
                          type="date"
                          value={simulatorData.startDate}
                          onChange={(e) => setSimulatorData({
                            ...simulatorData,
                            startDate: e.target.value,
                            endDate: e.target.value,
                            startTime: venue.openTime || '08:00',
                            endTime: venue.closeTime || '22:00'
                          })}
                        />
                      </div>
                      <div className="text-sm text-brand-ivory/60 bg-brand-ink/30 p-3 rounded-lg">
                        <Clock className="w-4 h-4 inline-block mr-2 text-brand-gold" />
                        Horário: {venue.openTime || '08:00'} às {venue.closeTime || '22:00'}
                      </div>
                    </>
                  )}
                </div>

                <Button
                  onClick={handleCheckAvailability}
                  variant="premium"
                  className="w-full"
                  disabled={!simulatorData.startDate || !simulatorData.endDate || checkingAvailability}
                >
                  {checkingAvailability ? 'Verificando...' : 'Verificar Disponibilidade'}
                </Button>

                {/* Availability Error */}
                {availabilityChecked && availabilityError && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 text-sm">{availabilityError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alternative Suggestions */}
                {alternativeSuggestions.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-brand-gold font-semibold">Sugestões de Datas Disponíveis:</h4>
                    {alternativeSuggestions.map((alt, idx) => {
                      const date = new Date(alt.startDate + 'T12:00:00');
                      const dayName = daysOfWeekLabels[date.getDay()];
                      const formattedDate = date.toLocaleDateString('pt-BR');
                      return (
                        <button
                          key={idx}
                          onClick={() => handleSelectAlternative(alt)}
                          className="w-full p-3 text-left bg-brand-ink/50 border border-brand-gold/20 rounded-lg hover:border-brand-gold/50 transition-all"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="text-brand-ivory font-medium">{dayName}, {formattedDate}</div>
                              <div className="text-brand-ivory/60 text-sm">{alt.startTime} às {alt.endTime}</div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-brand-gold" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Availability Success */}
                {availabilityChecked && isAvailable && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Horário disponível!</span>
                    </div>
                  </div>
                )}

                {priceCalculation && isAvailable && (
                  <div className="pt-4 border-t border-brand-gold/20">
                    <div className="text-center mb-4">
                      <div className="text-sm text-brand-ivory/60 mb-1">Valor Total</div>
                      <div className="text-3xl font-bold text-brand-gold">
                        {formatPrice(priceCalculation.total)}
                      </div>
                    </div>

                    {showReservationForm && (
                      <div className="space-y-3 pt-4 border-t border-brand-gold/10">
                        <h4 className="font-semibold text-brand-gold">Seus Dados</h4>
                        <Input
                          placeholder="Nome completo *"
                          value={reservationData.customerName}
                          onChange={(e) => setReservationData({ ...reservationData, customerName: e.target.value })}
                        />
                        <Input
                          type="email"
                          placeholder="E-mail *"
                          value={reservationData.customerEmail}
                          onChange={(e) => setReservationData({ ...reservationData, customerEmail: e.target.value })}
                        />
                        <Input
                          placeholder="Telefone *"
                          value={reservationData.customerPhone}
                          onChange={(e) => setReservationData({ ...reservationData, customerPhone: e.target.value })}
                        />
                        <textarea
                          placeholder="Observações (opcional)"
                          rows={3}
                          value={reservationData.notes}
                          onChange={(e) => setReservationData({ ...reservationData, notes: e.target.value })}
                          className="w-full p-2 bg-brand-ink border border-brand-gold/20 rounded text-brand-ivory"
                        />
                        <Button
                          onClick={handleReservation}
                          variant="premium"
                          className="w-full"
                          disabled={
                            !reservationData.customerName ||
                            !reservationData.customerEmail ||
                            !reservationData.customerPhone ||
                            submitting
                          }
                        >
                          {submitting ? 'Enviando...' : 'Solicitar Reserva'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
